/*
  Far & Away functional tableware landing page (sections/fa-fun-*.liquid).
  Plain JavaScript, no dependencies. Reproduces the behaviour of the Replo page for the blocks that are
  unique to this page: the set selector (set tabs, colour swatches, colour dropdown, quantity, prices,
  per-colour image carousels, Klarna amount, add to cart through the theme's cart drawer), the image
  carousels, the product accordions, the "Meet the Makers" video player, the lazy-started background
  videos, the scroll-to buttons and the currency conversion of the delivery copy.
  The blocks shared with the bundles page (press, marquee, customer photos, reviews, FAQ, risk free) are
  the fa-lp-* sections and are driven by assets/fa-lp.js; the hooks here use the data-ff-* prefix so the
  two scripts never touch each other's elements.
*/
(function () {
  'use strict';

  var all = function (sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); };
  var classes = function (el, add, remove) {
    (remove || '').split(' ').forEach(function (c) { if (c) el.classList.remove(c); });
    (add || '').split(' ').forEach(function (c) { if (c) el.classList.add(c); });
  };

  /* Money formatting: the set selector renders 123456 cents through Liquid's money filter
     (data-ff-money-sample, e.g. "€1.234,56", "£1,234.56", "$1,234.56") and prefix, separators and
     suffix are read from it, so prices re-rendered here match the server-rendered ones. */
  var money = { prefix: '$', thousands: ',', decimal: '.', decimals: true, suffix: '' };
  function learnMoney(sample) {
    var m = sample && sample.match(/^(\D*)1(\D*)234(?:(\D*)56)?(\D*)$/);
    if (!m) return;
    money = { prefix: m[1], thousands: m[2], decimal: m[3] || '', decimals: !!m[3], suffix: m[4] };
  }
  function formatMoney(cents, fmt) {
    var n = (Number(cents) || 0) / 100;
    var parts = (money.decimals ? n.toFixed(2) : String(Math.round(n))).split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, money.thousands);
    var dec = parts[1] || '';
    if (fmt === 'nz' && /^0*$/.test(dec)) dec = ''; // money_without_trailing_zeros drops ",00"
    return money.prefix + whole + (dec ? money.decimal + dec : '') + money.suffix;
  }

  /* Delivery copy in presentment currencies other than the store currency (and GBP, which has fixed
     amounts): convert the store-currency thresholds at Shopify.currency.rate, formatted the same way
     as faApplyFreeShipping() in the theme's snippets/render.liquid. */
  function initDelivery(scope) {
    all('[data-ff-ship-convert]', scope).forEach(function (section) {
      var cur = window.Shopify && window.Shopify.currency;
      var rate = cur ? parseFloat(cur.rate) : NaN;
      if (!cur || !cur.active || !(rate > 0)) return;
      var symbols = { CHF: 'CHF ', CZK: 'Kč ', DKK: 'kr ', HUF: 'Ft ', PLN: 'zł ', SEK: 'kr ', USD: '$', EUR: '€', GBP: '£' };
      var fmt = function (base) {
        var v = Math.round(base * rate * 100) / 100;
        return (symbols[cur.active] || cur.active + ' ') + (v % 1 === 0 ? String(v) : v.toFixed(2));
      };
      var free = parseFloat(section.getAttribute('data-ff-ship-free-base'));
      var cost = parseFloat(section.getAttribute('data-ff-ship-cost-base'));
      if (free > 0) all('[data-ff-ship="free"]', section).forEach(function (el) { el.textContent = fmt(free); });
      if (cost > 0) all('[data-ff-ship="cost"]', section).forEach(function (el) { el.textContent = fmt(cost); });
    });
  }

  /* ---------------- Collapsibles (product accordions) ---------------- */
  function initCollapsibles(scope) {
    all('[data-ff-coll]', scope).forEach(function (coll) {
      if (coll.__ff) return; coll.__ff = true;
      var trigger = coll.querySelector('[data-ff-coll-trigger]');
      var content = coll.querySelector('[data-ff-coll-content]');
      if (!trigger || !content) return;
      var toggle = function () {
        var open = trigger.getAttribute('aria-expanded') !== 'true';
        trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
        coll.setAttribute('data-state', open ? 'open' : 'closed');
        if (open) { content.removeAttribute('hidden'); content.setAttribute('data-state', 'open'); }
        else { content.setAttribute('hidden', ''); content.setAttribute('data-state', 'closed'); }
        var cls = trigger.getAttribute('data-ff-open-class');
        if (cls) trigger.classList.toggle(cls, open);
        all('path[data-ff-d-open]', trigger).forEach(function (p) {
          if (!p.hasAttribute('data-ff-d-closed')) p.setAttribute('data-ff-d-closed', p.getAttribute('d'));
          p.setAttribute('d', open ? p.getAttribute('data-ff-d-open') : p.getAttribute('data-ff-d-closed'));
        });
      };
      trigger.addEventListener('click', toggle);
      trigger.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } });
    });
  }

  /* ---------------- Carousels ---------------- */
  function Carousel(root) {
    this.root = root;
    this.index = 0;
    this.viewports = all('[data-ff-part="slides"]', root);
    this.prev = all('[data-ff-part="nav-button"][data-ff-dir="previous"]', root);
    this.next = all('[data-ff-part="nav-button"][data-ff-dir="next"]', root);
    this.dots = all('[aria-label^="Go to slide"]', root);
    // active classes of dots = classes on the first (active) dot that the second one lacks
    this.dotActive = [];
    if (this.dots.length > 1) {
      var a = this.dots[0].firstElementChild.classList, b = this.dots[1].firstElementChild.classList;
      for (var i = 0; i < a.length; i++) if (!b.contains(a[i])) this.dotActive.push(a[i]);
    }
    // same for thumbnail slides (the first thumbnail carries the active class)
    this.thumbActive = [];
    this.viewports.forEach(function (vp) {
      var slides = all('[data-ff-part="slide-track"] > [data-ff-part="slide"]', vp);
      if (vp.getAttribute('data-ff-thumbs') !== null && slides.length > 1) {
        var x = slides[0].firstElementChild.classList, y = slides[1].firstElementChild.classList;
        for (var k = 0; k < x.length; k++) if (!y.contains(x[k])) this.thumbActive.push(x[k]);
      }
    }, this);
    var self = this;
    this.prev.forEach(function (b) { b.addEventListener('click', function () { self.go(self.index - 1); }); });
    this.next.forEach(function (b) { b.addEventListener('click', function () { self.go(self.index + 1); }); });
    this.dots.forEach(function (d, i) {
      d.addEventListener('click', function () { self.go(i); });
      d.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); self.go(i); } });
    });
    this.viewports.forEach(function (vp) {
      self.swipe(vp);
      if (vp.getAttribute('data-ff-thumbs') !== null) all('[data-ff-part="slide"]', vp).forEach(function (s, i) {
        s.addEventListener('click', function () { if (!vp.__ffDragged) self.go(i); });
      });
    });
    window.addEventListener('resize', function () { self.go(self.index, true); });
  }
  Carousel.prototype.visible = function (track) {
    return all(':scope > [data-ff-part="slide"]', track).filter(function (s) { return s.style.display !== 'none'; });
  };
  Carousel.prototype.metrics = function (vp) {
    var track = vp.querySelector('[data-ff-part="slide-track"]');
    if (!track || !vp.offsetWidth) return null;
    var slides = this.visible(track);
    if (!slides.length) return null;
    var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
    var w = slides[0].getBoundingClientRect().width;
    var vw = vp.getBoundingClientRect().width;
    var perPage = Math.max(1, Math.round((vw + gap) / (w + gap)));
    return { track: track, slides: slides, step: w + gap, max: Math.max(0, slides.length - perPage), count: slides.length };
  };
  Carousel.prototype.count = function () {
    var n = 0; this.viewports.forEach(function (vp) { var m = this.metrics(vp); if (m) n = Math.max(n, m.max); }, this); return n;
  };
  Carousel.prototype.go = function (i, instant) {
    var max = this.count();
    i = Math.max(0, Math.min(i, max));
    this.index = i;
    var self = this;
    this.viewports.forEach(function (vp) {
      var m = this.metrics(vp); if (!m) return;
      var idx = Math.min(i, m.max);
      m.track.style.transitionProperty = instant ? 'none' : 'transform';
      m.track.style.transitionDuration = instant ? '0ms' : '250ms';
      m.track.style.transitionTimingFunction = 'ease';
      var x = m.slides[idx] ? m.slides[idx].offsetLeft - m.slides[0].offsetLeft : idx * m.step;
      m.track.style.transform = 'translate3d(' + (-x) + 'px, 0px, 0px)';
      m.slides.forEach(function (s, k) {
        if (k === i) { s.removeAttribute('aria-hidden'); s.setAttribute('data-ff-active', ''); } else { s.setAttribute('aria-hidden', 'true'); s.removeAttribute('data-ff-active'); }
        if (vp.getAttribute('data-ff-thumbs') !== null) self.thumbActive.forEach(function (c) { s.firstElementChild.classList.toggle(c, k === i); });
      });
    }, this);
    this.prev.forEach(function (b) { self.disable(b, i <= 0); });
    this.next.forEach(function (b) { self.disable(b, i >= max); });
    this.dots.forEach(function (d, k) {
      self.dotActive.forEach(function (c) { d.firstElementChild.classList.toggle(c, k === i); });
      d.setAttribute('aria-current', k === i ? 'true' : 'false');
    });
  };
  Carousel.prototype.disable = function (btn, off) {
    var cls = btn.getAttribute('data-ff-dis');
    if (cls) btn.classList.toggle(cls, off);
    if (off) btn.setAttribute('disabled', ''); else btn.removeAttribute('disabled');
  };
  Carousel.prototype.swipe = function (vp) {
    var self = this, startX = 0, startY = 0, dx = 0, active = false, horizontal = null, base = 0, m = null;
    var down = function (x, y) { m = self.metrics(vp); if (!m) return; active = true; horizontal = null; startX = x; startY = y; dx = 0;
      var cur = new DOMMatrixReadOnly(getComputedStyle(m.track).transform); base = cur.m41; };
    var move = function (x, y, e) {
      if (!active) return;
      dx = x - startX; var dy = y - startY;
      if (horizontal === null && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) horizontal = Math.abs(dx) > Math.abs(dy);
      if (!horizontal) return;
      if (e && e.cancelable) e.preventDefault();
      m.track.style.transitionProperty = 'none'; m.track.style.transitionDuration = '0ms';
      m.track.style.transform = 'translate3d(' + (base + dx) + 'px, 0px, 0px)';
    };
    var up = function () {
      if (!active) return; active = false;
      if (!horizontal) return;
      var threshold = Math.min(60, (m ? m.step : 300) * 0.2);
      if (dx < -threshold) self.go(self.index + 1); else if (dx > threshold) self.go(self.index - 1); else self.go(self.index);
      vp.__ffDragged = Math.abs(dx) > 6;
      window.setTimeout(function () { vp.__ffDragged = false; }, 50);
    };
    vp.addEventListener('touchstart', function (e) { var t = e.touches[0]; down(t.clientX, t.clientY); }, { passive: true });
    vp.addEventListener('touchmove', function (e) { var t = e.touches[0]; move(t.clientX, t.clientY, e); }, { passive: false });
    vp.addEventListener('touchend', up);
    vp.addEventListener('touchcancel', up);
    vp.addEventListener('mousedown', function (e) { if (e.button !== 0) return; down(e.clientX, e.clientY); e.preventDefault(); });
    window.addEventListener('mousemove', function (e) { move(e.clientX, e.clientY); });
    window.addEventListener('mouseup', up);
    vp.addEventListener('click', function (e) { if (vp.__ffDragged) { e.preventDefault(); e.stopPropagation(); } }, true);
  };

  /* ---------------- Set selector ---------------- */
  function initBundle(section) {
    var parent = section.querySelector('[data-ff-bundle]');
    if (!parent) return;
    var states = JSON.parse(parent.getAttribute('data-ff-states') || '{}');
    learnMoney(parent.getAttribute('data-ff-money-sample'));
    var stateClasses = Object.keys(states).map(function (k) { return states[k]; }).filter(Boolean);

    all('[data-ff-product]', parent).forEach(function (box) {
      var data;
      try { data = JSON.parse(box.querySelector('[data-ff-data]').textContent); } catch (e) { return; }
      var form = box.querySelector('[data-ff-form]');
      var carouselRoot = box.querySelector('[data-ff-carousel]');
      var carousel = carouselRoot && carouselRoot.__ffCarousel;
      var options = all('[data-ff-swatches] [role="option"]', box);
      var select = box.querySelector('[data-ff-select]');
      var qty = 1;
      var selectedCls = [];
      var sel = options.filter(function (o) { return o.getAttribute('aria-selected') === 'true'; })[0];
      var other = options.filter(function (o) { return o !== sel; })[0];
      if (sel && other) for (var i = 0; i < sel.classList.length; i++) if (!other.classList.contains(sel.classList[i])) selectedCls.push(sel.classList[i]);
      var current = data.variants.filter(function (v) { return v.id === data.variant; })[0] || data.variants[0];

      function withWidth(src, ref) {
        var w = (ref || '').match(/[?&]width=\d+/);
        if (!w || src.indexOf('cdn.shopify.com') < 0 && src.indexOf('/cdn/shop/') < 0) return src;
        return src + (src.indexOf('?') > -1 ? '&' : '?') + w[0].slice(1);
      }
      function setImages(color) {
        var list = data.images[color];
        if (!list) return;
        all('[data-ff-part="slide-track"]', box).forEach(function (track) {
          var slides = all(':scope > [data-ff-part="slide"]', track);
          while (slides.length < list.length) { var c = slides[slides.length - 1].cloneNode(true); track.appendChild(c); slides.push(c); }
          slides.forEach(function (slide, k) {
            var src = list[k];
            slide.style.display = src ? 'flex' : 'none';
            if (!src) return;
            var pic = slide.querySelector('picture');
            if (pic) pic.style.setProperty('--fa-fun-attributes-currentitem', 'url("' + src + '")');
            all('source', slide).forEach(function (s) { s.setAttribute('srcset', withWidth(src, s.getAttribute('srcset'))); });
            var img = slide.querySelector('img'); if (img) img.setAttribute('src', withWidth(src, img.getAttribute('src')));
          });
        });
        var dots = all('[aria-label^="Go to slide"]', box);
        dots.forEach(function (d, k) {
          d.style.display = list[k] ? 'contents' : 'none';
          if (list[k]) d.firstElementChild.style.setProperty('--fa-fun-attributes-currentitem', 'url("' + list[k] + '")');
        });
      }

      function render(variant) {
        current = variant;
        if (form) form.querySelector('[name="id"]').value = variant.id;
        all('[data-ff-compare]', box).forEach(function (el) { (el.querySelector('.money') || el).textContent = formatMoney(variant.compare, el.getAttribute('data-ff-fmt')); });
        all('[data-ff-price]', box).forEach(function (el) { (el.querySelector('.money') || el).textContent = formatMoney(variant.price, el.getAttribute('data-ff-fmt')); });
        all('[data-ff-diff]', box).forEach(function (el) { (el.querySelector('.money') || el).textContent = formatMoney(variant.compare - variant.price, el.getAttribute('data-ff-fmt')); });
        all('[data-ff-color-label]', box).forEach(function (el) { (el.querySelector('span') || el).textContent = variant.color; });
        all('[data-ff-klarna]', box).forEach(function (el) { el.setAttribute('data-purchase-amount', variant.price); });
        if (window.Klarna && window.Klarna.OnsiteMessaging && window.Klarna.OnsiteMessaging.refresh) { try { window.Klarna.OnsiteMessaging.refresh(); } catch (e) {} }
        all('[data-ff-oos-class]', box).forEach(function (el) { el.classList.toggle(el.getAttribute('data-ff-oos-class'), !variant.available); });
        options.forEach(function (o) {
          var on = o.getAttribute('data-ff-value') === variant.color;
          o.setAttribute('aria-selected', on ? 'true' : 'false');
          selectedCls.forEach(function (c) { o.classList.toggle(c, on); });
        });
        if (select) select.value = variant.color;
      }

      function pickColor(color) {
        var v = data.variants.filter(function (x) { return x.color === color; })[0];
        if (!v || v === current) return;
        render(v); setImages(v.color);
        if (carousel) carousel.go(0, true);
      }
      options.forEach(function (o) {
        var pick = function () { pickColor(o.getAttribute('data-ff-value')); };
        o.addEventListener('click', pick);
        o.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(); } });
      });
      if (select) select.addEventListener('change', function () { pickColor(select.value); });

      all('[data-ff-qty]', box).forEach(function (btn) {
        var step = function () {
          qty = Math.max(1, qty + (parseInt(btn.getAttribute('data-ff-qty'), 10) || 0));
          all('[data-ff-qty-value]', box).forEach(function (el) { (el.querySelector('span') || el).textContent = String(qty); });
          if (form) form.querySelector('[name="quantity"]').value = qty;
        };
        btn.addEventListener('click', step);
        btn.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); step(); } });
      });

      all('[data-ff-tab]', box).forEach(function (tab) {
        var pick = function () {
          var key = tab.getAttribute('data-ff-tab');
          stateClasses.forEach(function (c) { parent.classList.remove(c); });
          if (states[key]) parent.classList.add(states[key]);
          window.requestAnimationFrame(function () { all('[data-ff-carousel]', parent).forEach(function (c) { if (c.__ffCarousel) c.__ffCarousel.go(c.__ffCarousel.index, true); }); });
        };
        tab.addEventListener('click', pick);
      });

      all('[data-ff-atc]', box).forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          addToCart(current.id, qty, form);
        });
      });

      var title = box.querySelector('[data-ff-href]');
      if (title) {
        var openTitle = function () { window.open(title.getAttribute('data-ff-href'), '_blank'); };
        title.addEventListener('click', openTitle);
        title.addEventListener('keydown', function (e) { if (e.key === 'Enter') openTitle(); });
      }
      box.__ffRender = render;
    });
  }

  function addToCart(id, qty, form) {
    var loader = document.querySelector('.loader');
    if (loader) loader.classList.remove('hide_loader');
    var done = function () { if (loader) loader.classList.add('hide_loader'); };
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: id, quantity: qty }] })
    }).then(function (r) {
      if (!r.ok) throw new Error('add failed ' + r.status);
      return r.json();
    }).then(function () {
      // Same path as the theme's own product page: refresh the cart drawer and open it.
      if (typeof window.updatecartnew === 'function') window.updatecartnew('/cart/update');
      else { done(); window.location.href = root + 'cart'; }
    }).catch(function () {
      done();
      if (form) form.submit();
    });
  }

  /* ---------------- Video player (Meet the Makers) ---------------- */
  function initVideo(box) {
    var video = box.querySelector('video');
    if (!video) return;
    var play = box.querySelector('[data-ff-video-play]');
    var mute = box.querySelector('[data-ff-video-mute]');
    var fs = box.querySelector('[data-ff-video-fs]');
    var overlay = box.querySelector('[data-ff-video-overlay]');
    var swap = function (btn, alt) {
      var p = btn.querySelector('path'); if (!p) return;
      if (!p.hasAttribute('data-ff-d-closed')) p.setAttribute('data-ff-d-closed', p.getAttribute('d'));
      p.setAttribute('d', alt ? btn.getAttribute('data-ff-d-alt') : p.getAttribute('data-ff-d-closed'));
    };
    var start = function () {
      if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
      var p = video.play(); if (p && p.catch) p.catch(function () {}); // a pause() right after play() rejects the promise
    };
    var toggle = function () { if (video.paused) start(); else video.pause(); };
    video.addEventListener('play', function () { if (play) { swap(play, true); play.querySelector('svg').setAttribute('aria-label', 'Pause'); } });
    video.addEventListener('pause', function () { if (play) { swap(play, false); play.querySelector('svg').setAttribute('aria-label', 'Play'); } });
    if (play) play.addEventListener('click', toggle);
    if (overlay) overlay.addEventListener('click', start);
    video.addEventListener('click', toggle);
    if (mute) mute.addEventListener('click', function () {
      video.muted = !video.muted;
      swap(mute, video.muted);
      mute.querySelector('svg').setAttribute('aria-label', video.muted ? 'Unmute' : 'Mute');
    });
    if (fs) fs.addEventListener('click', function () {
      var el = video;
      if (document.fullscreenElement) document.exitFullscreen();
      else if (el.requestFullscreen) el.requestFullscreen();
      else if (el.webkitEnterFullscreen) el.webkitEnterFullscreen();
    });
    [play, mute, fs].forEach(function (b) {
      if (b) b.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); b.click(); } });
    });
  }

  /* ---------------- Lazy background videos ----------------
     Same pattern as the EU theme's layout script (preload="none" + data-lazyvideo, started when scrolled
     into view, never on a metered connection). Repeated here so the US theme, which has no such script,
     behaves the same; a video that is already playing is left alone. */
  function initLazyVideos(scope) {
    var vids = all('video[data-lazyvideo]', scope);
    if (!vids.length) return;
    var c = navigator.connection || {};
    if (c.saveData === true || /^(slow-2g|2g|3g)$/.test(c.effectiveType || '')) return;
    if (!('IntersectionObserver' in window)) { vids.forEach(function (v) { v.muted = true; var p = v.play(); if (p && p.catch) p.catch(function () {}); }); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var v = e.target; io.unobserve(v);
        if (!v.paused) return;
        v.preload = 'auto'; v.muted = true; v.setAttribute('playsinline', '');
        var p = v.play(); if (p && p.catch) p.catch(function () {});
      });
    }, { rootMargin: '200px 0px' });
    vids.forEach(function (v) { io.observe(v); });
  }

  /* ---------------- Scroll buttons ---------------- */
  function initScroll(scope) {
    all('[data-ff-scroll]', scope).forEach(function (el) {
      if (el.__ffScroll) return; el.__ffScroll = true;
      el.addEventListener('click', function (e) {
        var sel = el.getAttribute('data-ff-scroll') || '';
        // ids such as "5reasons" are not valid CSS selectors, so look them up by id
        var target = sel.charAt(0) === '#' ? document.getElementById(sel.slice(1)) : document.querySelector(sel);
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  function init() {
    var scope = document;
    initCollapsibles(scope);
    all('[data-ff-carousel]', scope).forEach(function (c) {
      if (c.__ffCarousel) return;
      c.__ffCarousel = new Carousel(c);
      var box = c.closest('[data-ff-product]'); if (box) box.__ffCarousel = c.__ffCarousel;
    });
    all('[data-ff-section="bundle"]', scope).forEach(initBundle);
    all('[data-ff-video]', scope).forEach(initVideo);
    initLazyVideos(scope);
    initScroll(scope);
    initDelivery(scope);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
